<template>
  <Transition
    enter-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    leave-active-class="transition-opacity duration-200"
    leave-to-class="opacity-0"
  >
    <button
      v-show="!store.isOpen"
      :aria-label="i18n(translations.ariaLabel)"
      :class="[
        'bg-static-ai-mid fixed right-24 z-[9] flex items-center gap-8 rounded-lg p-12 shadow-middle transition-[bottom] duration-300',
        fabPositionClass,
      ]"
      data-test="shopping-assistant-fab"
      :style="fabBottomStyle"
      type="button"
      @click="handleClick"
    >
      <IconCommentWithSparkles size="24" />

      <!-- Text content: only on desktop -->
      <span class="hidden body-2-bold text-onaction-ai-mid md:flex">
        {{ i18n(translations.message) }}
      </span>
    </button>
  </Transition>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { IconCommentWithSparkles } from '@ds/icons/IconCommentWithSparkles'

import { useStickyBarBottomOffset } from '~/composables/useStickyBarBottomOffset'

import { useShoppingAssistantWebSidebar } from '../stores/useShoppingAssistantWebSidebar'

import translations from './WebSidebarShoppingAssistantFAB.translations'

// Small visual gap kept above the sticky bar's own rendered height.
const FAB_CLEARANCE_MARGIN_PX = 8

const i18n = useI18n()
const tracking = useTracking()
const store = useShoppingAssistantWebSidebar()
const { stickyBarBottomHeight } = useStickyBarBottomOffset()

const isStickyBarBottomVisible = computed(() => stickyBarBottomHeight.value > 0)

const fabBottomStyle = computed(() =>
  isStickyBarBottomVisible.value
    ? { bottom: `${stickyBarBottomHeight.value + FAB_CLEARANCE_MARGIN_PX}px` }
    : undefined,
)

// `fabBottomStyle` clears the actual rendered height of whichever bottom
// `RevStickyBar` is currently on screen (see useStickyBarBottomHeightLogic),
// so it stays correct regardless of that bar's own content or breakpoints —
// e.g. it's naturally 0 once the bar's own responsive classes hide it,
// since a `display: none` element reports a 0×0 box to the ResizeObserver
// that feeds this height. Not every bar hides at the same breakpoints (some
// stay visible on desktop or short viewports), so this can't hardcode a
// breakpoint-based CSS fallback the way a single PDP-only bar could. Easing
// mirrors RevStickyBar's own enter/leave transition (ease-in in, ease-out
// out) so both move in sync instead of the FAB outrunning the bar.
const fabPositionClass = computed(() =>
  isStickyBarBottomVisible.value ? 'ease-in' : 'ease-out bottom-24',
)

function handleClick() {
  store.open({ entryPoint: 'floating_button' })
  tracking.trackClick({
    zone: 'floating_button',
    name: 'ai_shopping_assistant_opened',
    entryPoint: 'floating_button',
    seeded: false,
  })
}
</script>
